import { createBrowserRouter, Navigate } from "react-router";

import { RequireAuth } from "@/auth/RequireAuth";
import { DashboardIndex, RequireLayer } from "@/components/dashboard/RequireLayer";
import { AppShell } from "@/components/layout/AppShell";
import { NotFound } from "@/pages/NotFound";
import { Placeholder } from "@/pages/Placeholder";
import { SignIn } from "@/pages/SignIn";

/**
 * De routetabel volgt de navigatiestructuur uit `ui-overzicht.md`.
 *
 * Op het bestuurdersdashboard, het objectscherm en de stijlgids na is elk scherm nog een placeholder. Dat is de opzet van dit
 * fundament: de schil, de tokens en de componenten staan, de schermen met echte
 * data volgen in een vervolg-PR. Elke placeholder noemt welk prototypescherm of
 * welke module hij gaat worden, zodat de volgorde later geen giswerk is.
 */
export const router = createBrowserRouter([
  {
    // Het enige scherm buiten de bewaking; niet lui geladen, want het is het
    // eerste dat een uitgelogde bezoeker te zien krijgt.
    path: "/inloggen",
    element: <SignIn />,
  },
  {
    // Eén bewaking om de hele applicatie heen. Een nieuw scherm hieronder valt
    // er automatisch achter — dezelfde keuze als `enforce_write_access` op de
    // backendrouter.
    element: <RequireAuth />,
    children: [
      {
        path: "/",
        element: <AppShell />,
        children: [
      {
        // Het dashboard per sturingslaag (K10, bouwspec §6): "/" en "/dashboard"
        // sturen door naar de laatst gebruikte of eerste toegestane laag.
        index: true,
        element: <DashboardIndex />,
      },
      {
        path: "dashboard",
        element: <DashboardIndex />,
      },
      {
        // Lui geladen om dezelfde reden als de stijlgids: recharts is de enige
        // zware afhankelijkheid en hoort niet in de eerste bundel van iemand
        // die rechtstreeks naar een ander scherm navigeert. `RequireLayer`
        // stuurt een laag buiten de eigen sturingsrollen door, met een melding.
        path: "dashboard/:laag",
        lazy: async () => {
          const { Dashboard } = await import("@/pages/Dashboard");
          return {
            Component: () => (
              <RequireLayer>{(laag, lagen) => <Dashboard layer={laag} layers={lagen} />}</RequireLayer>
            ),
          };
        },
      },
      {
        // Scherm 03: de lijst is de ingang, het objectscherm de detailpagina.
        // Allebei lui geladen — ze trekken recharts mee voor de
        // onderhoudsgrafiek, net als het dashboard.
        path: "objecten",
        lazy: async () => {
          const { Objects } = await import("@/pages/Objects");
          return { Component: Objects };
        },
      },
      {
        path: "objecten/:assetId",
        lazy: async () => {
          const { AssetDetail } = await import("@/pages/AssetDetail");
          return { Component: AssetDetail };
        },
      },
      {
        // Scherm 04. Lui geladen, net als de objectenlijst: het deelt de
        // filterbediening en de tabel met scherm 03.
        path: "complexen",
        lazy: async () => {
          const { Complexes } = await import("@/pages/Complexes");
          return { Component: Complexes };
        },
      },
      {
        // Scherm 05 (Jan, 10-09-2026). Lui geladen, net als de complexenlijst:
        // het deelt de filterbediening en de tabel.
        path: "panden",
        lazy: async () => {
          const { Buildings } = await import("@/pages/Buildings");
          return { Component: Buildings };
        },
      },
      {
        // Eén pand (FB-087): de gegevens van het objectscherm, opgeteld over de
        // eenheden, met de eenheden eronder. Lui geladen: recharts.
        path: "panden/:buildingId",
        lazy: async () => {
          const { BuildingDetail } = await import("@/pages/BuildingDetail");
          return { Component: BuildingDetail };
        },
      },
      {
        // De hiërarchie én de deelportefeuilles die de gebruiker zelf bewaart
        // (Jan, 08-09). Lui geladen omdat het scherm de filterbediening van de
        // objectenlijst meeneemt.
        path: "portefeuille",
        lazy: async () => {
          const { Portfolio } = await import("@/pages/Portfolio");
          return { Component: Portfolio };
        },
      },
      {
        // Scherm 06 (module 04). Lui geladen: maplibre-gl is met afstand de
        // zwaarste afhankelijkheid en hoort niet in de eerste bundel van iemand
        // die naar het dashboard navigeert.
        path: "kaart",
        lazy: async () => {
          const { PortfolioMap } = await import("@/pages/PortfolioMap");
          return { Component: PortfolioMap };
        },
      },
      {
        // Levering Jan 17-09-2026: STIKO op NEN 2699 per programma, de
        // ontwikkelroute en de investeringskasstroom. Lui geladen: recharts.
        path: "projecten",
        lazy: async () => {
          const { Projects } = await import("@/pages/Projects");
          return { Component: Projects };
        },
      },
      {
        path: "scenarios",
        element: (
          <Placeholder
            title="Scenario's"
            description="Varianten naast elkaar, met delta's ten opzichte van de basis."
            origin="Wordt scherm 07 uit het prototype (module 08); leest de scenariolaag en de programma's P1 tot en met P22."
            needs="het scenarioscherm staat gepland voor de ronde na 00j (plan 23, besluit I7)."
          />
        ),
      },
      {
        // FB-078 deel 3: de conflictmatrix v0 (begin module 18). Lui geladen.
        path: "conflictmatrix",
        lazy: async () => {
          const { ConflictMatrix } = await import("@/pages/ConflictMatrix");
          return { Component: ConflictMatrix };
        },
      },
      {
        // Plan 12c: SAH-potentie per complex. Lui geladen, net als de andere schermen.
        path: "sah-potentie",
        lazy: async () => {
          const { SahPotential } = await import("@/pages/SahPotential");
          return { Component: SahPotential };
        },
      },
      {
        // Plan 12a: de WSW-aanlevering marktwaarde (deadline 1-11-2026). Lui
        // geladen, net als de andere schermen.
        path: "wsw-aanlevering",
        lazy: async () => {
          const { WswReadiness } = await import("@/pages/WswReadiness");
          return { Component: WswReadiness };
        },
      },
      {
        // Werksessie Jan 24-09-2026: hoe komt de beleidswaarde tot stand. Lui
        // geladen: recharts (de waterval).
        path: "beleidswaarde",
        lazy: async () => {
          const { PolicyValueBuildUp } = await import("@/pages/PolicyValueBuildUp");
          return { Component: PolicyValueBuildUp };
        },
      },
      {
        // De impact-waterfall (maandagbrief 24-08 §4). Lui geladen: hij trekt
        // recharts mee, net als het dashboard en het objectscherm.
        path: "parameterimpact",
        lazy: async () => {
          const { ParameterImpact } = await import("@/pages/ParameterImpact");
          return { Component: ParameterImpact };
        },
      },
      {
        // Besluit I2 (plan 23) en v4.0 §13: "Desinvestering" is de gebruikersterm.
        // Het oude adres blijft werken.
        path: "transformeren",
        element: <Navigate to="/desinvestering" replace />,
      },
      {
        // Plan 23: de dashboardkengetallen geordend naar strategisch, tactisch en operationeel.
        path: "kpis",
        lazy: async () => {
          const { Kpis } = await import("@/pages/Kpis");
          return { Component: Kpis };
        },
      },
      {
        // Bouwspec KD8 (ontwerp 40): de KPI-set met doelen, voor beheer.
        path: "kpi-set",
        lazy: async () => {
          const { KpiSet } = await import("@/pages/KpiSet");
          return { Component: KpiSet };
        },
      },
      {
        path: "rapportages",
        element: (
          <Placeholder
            title="Rapportages"
            description="Bestuurlijk, portefeuille, projecten, WSW/Aw, gemeenten en export."
            origin="Wordt scherm 11 uit het prototype (module 14, inclusief dPi/dVi-export)."
          />
        ),
      },
      {
        // Scherm 08 (module 11): de vastgestelde MJOB-60 over het bezit. Heet
        // sinds 20-10-2026 in het menu "Instandhouding", onder Begroting (plan 23). Lui
        // geladen: recharts, net als het dashboard.
        path: "onderhoud",
        lazy: async () => {
          const { Maintenance } = await import("@/pages/Maintenance");
          return { Component: Maintenance };
        },
      },
      {
        // Hoofdonderdeel "Verbetering (investering)" (Jan, 20-10-2026): het
        // verbeterdeel van alle ingrepen (plan 20) en "Project doorrekenen" (plan 19).
        path: "verbetering",
        lazy: async () => {
          const { Improvement } = await import("@/pages/Improvement");
          return { Component: Improvement };
        },
      },
      {
        // Plan 20: één invoer voor elke ingreep. Sinds plan 23 (I6) het menuonderdeel Ingrepen.
        path: "ingrepen/nieuw",
        lazy: async () => {
          const { InterventionNew } = await import("@/pages/InterventionNew");
          return { Component: InterventionNew };
        },
      },
      {
        // Module 13: huurprijsklassen, WWS-punten en het huurbeleid over het bezit.
        path: "huur",
        lazy: async () => {
          const { RentWws } = await import("@/pages/RentWws");
          return { Component: RentWws };
        },
      },
      {
        // Scherm 09 (module 12): labels, EFG-opgave, installaties en registratie.
        path: "duurzaamheid",
        lazy: async () => {
          const { Sustainability } = await import("@/pages/Sustainability");
          return { Component: Sustainability };
        },
      },
      {
        // Scherm 12. FB-052 (Jan, 23-09-2026): Leefbaarometer en veerkracht uit
        // elkaar. De huurniveaumix staat op /huurniveaus; hier alleen de
        // Leefbaarometer, per gemeente, wijk en buurt (plan 6b).
        path: "leefbaarheid",
        lazy: async () => {
          const { Livability } = await import("@/pages/Livability");
          return { Component: Livability };
        },
      },
      {
        // FB-054 (Jan, 01-10-2026): veerkracht is draaglast tegenover
        // draagkracht per gemeente, wijk en buurt (plan 6c).
        path: "veerkracht",
        lazy: async () => {
          const { Resilience } = await import("@/pages/Resilience");
          return { Component: Resilience };
        },
      },
      {
        // Scherm 13 (Jan, 14-09-2026): CBS Wijken en Buurten via PDOK.
        path: "cbs",
        lazy: async () => {
          const { Cbs } = await import("@/pages/Cbs");
          return { Component: Cbs };
        },
      },
      {
        // FB-046 (Jan, 23-09-2026): de omgeving van een complex of pand, met de
        // filters van de objectenlijst in de URL.
        path: "omgeving",
        lazy: async () => {
          const { AreaOverview } = await import("@/pages/AreaOverview");
          return { Component: AreaOverview };
        },
      },
      {
        // Scherm 14 (Jan, 14-09-2026; plan 15-09, C4): de verdeling over de
        // huurbanden per gebied. Heet sinds FB-054 (besluit Tom 01-10-2026)
        // "Huurniveaus en huurniveaumix".
        path: "huurniveaus",
        lazy: async () => {
          const { RentLevels } = await import("@/pages/RentLevels");
          return { Component: RentLevels };
        },
      },
      {
        // Scherm 17 (module 17, plan 9b): het gebiedspaspoort van een gemeente,
        // wijk of buurt, volgens de wijkscan Orden Apeldoorn.
        path: "wijkscan",
        lazy: async () => {
          const { AreaScan } = await import("@/pages/AreaScan");
          return { Component: AreaScan };
        },
      },
      {
        path: "data-ai",
        element: (
          <Placeholder
            title="Databronnen & datakwaliteit"
            description="Per bron de status, peildatum en dekking; per object, pand en complex een datakwaliteitsscore, ook als kaartlaag en filter."
            origin="Master-architectuur v4.0 §15."
            needs="een besluit over de weging van de datakwaliteitsscore (voorstel van ons, veto Jan). Herkomst per veld, peildata en terugvalmerkjes bestaan al."
          />
        ),
      },
      {
        // Module 09 woont hier: parameter- en indexbeheer, bronnen en rechten.
        // De werkvoorraad van meelezersopmerkingen. Bewust niet in de zijbalk:
        // die gaat over de portefeuille, dit over het platform zelf. De ingang
        // zit in het gebruikersmenu (Header.tsx).
        path: "feedback",
        lazy: async () => {
          const { Feedback } = await import("@/pages/Feedback");
          return { Component: Feedback };
        },
      },
      {
        // Eén ticket met zijn tijdlijn (Tom, 15-09-2026): /feedback/FB-012.
        path: "feedback/:ticket",
        lazy: async () => {
          const { FeedbackTicket } = await import("@/pages/FeedbackTicket");
          return { Component: FeedbackTicket };
        },
      },
      {
        // Gebruikers en rechten (29-09-2026): alleen voor een beheerder; de
        // ingang zit in het gebruikersmenu, net als de feedback.
        path: "gebruikers",
        lazy: async () => {
          const { Users } = await import("@/pages/Users");
          return { Component: Users };
        },
      },
      {
        // Ontwerp 28-10-2026: herberekenen en rekenstatus als beheerscherm,
        // onzichtbaar voor een meelezer. Lui geladen, net als de andere schermen.
        path: "rekenstatus",
        lazy: async () => {
          const { Rekenstatus } = await import("@/pages/Rekenstatus");
          return { Component: Rekenstatus };
        },
      },
      {
        // Ontwerp 28-10-2026, scherm 36: alle schermen die nog gebouwd moeten
        // worden, per onderdeel, uit `fixtures/navigation.ts`.
        path: "nog-te-bouwen",
        lazy: async () => {
          const { NogTeBouwen } = await import("@/pages/NogTeBouwen");
          return { Component: NogTeBouwen };
        },
      },
      {
        path: "instellingen",
        lazy: async () => {
          const { Settings } = await import("@/pages/Settings");
          return { Component: Settings };
        },
      },
      {
        // De stijlgids trekt recharts mee en is het enige scherm dat dat
        // vandaag doet. Apart laden houdt de eerste bundel klein; zodra een
        // echt scherm grafieken toont, verhuist recharts vanzelf naar een
        // gedeelde chunk.
        path: "styleguide",
        lazy: async () => {
          const { Styleguide } = await import("@/pages/styleguide/Styleguide");
          return { Component: Styleguide };
        },
      },
      // Master-architectuur v4.0 (oktober 2026): de onderdelen uit het hoofdmenu
      // die nog niet gebouwd zijn. Elke placeholder zegt wat ervoor nodig is.
      {
        // Bouwspec KD10a (ontwerp 41): de koerscyclus met ronde, varianten en de jaarcyclus.
        path: "koers",
        lazy: async () => {
          const { Koers } = await import("@/pages/Koers");
          return { Component: Koers };
        },
      },
      {
        path: "planning",
        element: (
          <Placeholder
            title="Planning & prognose"
            description="De meerjarige prognose van KPI's, kasstromen en ratio's tegen de planning."
            origin="Master-architectuur v4.0 §2 (Sturing)."
            needs="het scenarioscherm en de v70-kasstroomruns per scenario. De prognose van de beleidswaarde staat al onder Opbouw beleidswaarde en Parameterimpact."
          />
        ),
      },
      {
        path: "wens-game",
        element: (
          <Placeholder
            title="Kans / Wens-game"
            description="Gemeenten, horizon, huishoudens en aandeel kiezen en sturen op beschikbaarheid, betaalbaarheid, kwaliteit en veerkracht. De uitkomst is de wensportefeuille met PMC-mix en opgave."
            origin="Master-architectuur v4.0 §8 (module 18)."
            needs="de doelfunctie met gewichten per deelnemer (besluit 23-09: meerdere spelers) en een afspraak over de omvang, want het spel valt deels buiten module 18. De conflictmatrix onder Sturing is het begin."
          />
        ),
      },
      {
        // FB-106 (Jan, 28-10-2026): heette "Ontwerpen / Project-game". Het oude
        // adres blijft werken.
        path: "project-game",
        element: <Navigate to="/ontwerp-game" replace />,
      },
      {
        path: "ontwerp-game",
        element: (
          <Placeholder
            title="Programmeren / Ontwerp-game"
            description="Ontwerpen in 3D binnen de kaartcontext, met PMC, STIKO en een automatische haalbaarheidsstraat."
            origin="Master-architectuur v4.0 §9 tot en met §11."
            needs="een architectuurbesluit (ADR) over de IFC-editor: BonsaiBIM is een Blender-add-on en draait niet in de browser. De STIKO-doorrekening per project staat al onder Projectportfolio."
          />
        ),
      },
      {
        path: "portefeuille-game",
        element: (
          <Placeholder
            title="Impact / Portefeuille-game"
            description="Bestaand bezit, projecten, ingrepen, verkoop, sloop en transformatie samen door de tijd, met doelbereik, ratio's, kasstromen en conflictmatrix."
            origin="Master-architectuur v4.0 §8."
            needs="het scenarioscherm en de doorrekening van scenario's naar portefeuilleniveau (kasstroom met en zonder project)."
          />
        ),
      },
      {
        // v4.0 §2 en §12: de levenscyclus over de hele portefeuille (leest GET /portfolio/lifecycle).
        path: "lifecycle",
        lazy: async () => {
          const { PortfolioLifecycle } = await import("@/pages/PortfolioLifecycle");
          return { Component: PortfolioLifecycle };
        },
      },
      {
        path: "conditie",
        element: (
          <Placeholder
            title="Conditie"
            description="Conditiescores NEN 2767 per component, pand en complex, met inspectiedatum en bron."
            origin="Master-architectuur v4.0 §2 (Ingrepen & Lifecycle)."
            needs="het scherm op de vastgelegde condities, en voor een dekkend beeld conditiemetingen per element van de corporatie (nu alleen de O-Prognose-score per complex)."
          />
        ),
      },
      {
        // v4.0 §2 (Ingrepen & Lifecycle); plan 23, V2: de centrale bibliotheek met prijs en herkomst.
        path: "maatregelen",
        lazy: async () => {
          const { Measures } = await import("@/pages/Measures");
          return { Component: Measures };
        },
      },
      {
        path: "desinvestering",
        element: (
          <Placeholder
            title="Desinvestering"
            description="Verkoop, uitponden, sloop en uit exploitatie nemen, met de verwachte impact op waarde en kasstroom."
            origin="Master-architectuur v4.0 §13; plan 23, besluit I2."
            needs="een scherm op de desinvesteringsprogramma's (P14 tot en met P17) in de scenariolaag; de rekenkern kent ze al."
          />
        ),
      },
      {
        path: "meerjarenbegroting",
        element: (
          <Placeholder
            title="Meerjarenbegroting"
            description="Instandhouding, verbetering en desinvestering per jaar, herleidbaar tot vastgoed, maatregel en scenario."
            origin="Master-architectuur v4.0 §13."
            needs="de drie stromen samen in één leesmodel per jaar; instandhouding en verbetering staan er al apart."
          />
        ),
      },
      {
        path: "begroting-vergelijken",
        element: (
          <Placeholder
            title="Actual, forecast en scenario"
            description="Werkelijk, begroot, prognose en scenario naast elkaar, met prijspeil, indexatie, btw en kasstroomeffect."
            origin="Master-architectuur v4.0 §13."
            needs="de werkelijke uitgaven (actuals) uit het ERP-systeem van de corporatie: aanleververzoek aan IJsseldal."
          />
        ),
      },
      {
        path: "huurpotentie",
        element: (
          <Placeholder
            title="Huurpotentie"
            description="Huidige huur tegenover beleidshuur, streefhuur en maximale huur, per object en opgeteld."
            origin="Master-architectuur v4.0 §14."
            needs="een eigen scherm op wat er al is (de toets aan het huurbeleid staat onder Huur & WWS), en voor een zuiver beeld een verse huurexport: de huren zijn van 1-7-2023."
          />
        ),
      },
      {
        path: "mutaties",
        element: (
          <Placeholder
            title="Mutaties"
            description="Mutatiegraad, mutatiedatum, doorlooptijd en mutatiekosten."
            origin="Master-architectuur v4.0 §14."
            needs="verhuurgegevens per VHE uit het verhuursysteem van de corporatie: aanleververzoek aan IJsseldal. Nu is alleen de mutatiegraad per tak uit de jaarrekening bekend."
          />
        ),
      },
      {
        path: "leegstand",
        element: (
          <Placeholder
            title="Leegstand"
            description="Leegstand naar type, duur en oorzaak."
            origin="Master-architectuur v4.0 §14."
            needs="leegstandsgegevens per VHE uit het verhuursysteem van de corporatie: aanleververzoek aan IJsseldal."
          />
        ),
      },
      {
        path: "derving",
        element: (
          <Placeholder
            title="Derving"
            description="Huurderving in euro en procent."
            origin="Master-architectuur v4.0 §14."
            needs="derving per VHE uit het verhuursysteem. Nu is alleen het percentage per tak uit de jaarrekening en de dVi bekend."
          />
        ),
      },
      {
        path: "woningmarkt",
        element: (
          <Placeholder
            title="Woningmarkt"
            description="Vraag en aanbod, wachttijden, prijzen en woningbehoefte in het werkgebied."
            origin="Master-architectuur v4.0 §2 (Omgeving)."
            needs="een bronkeuze voor de woningbehoefte: CBS (open) of ABF (betaalde licentie)."
          />
        ),
      },
      {
        // v4.0 §2 (Omgeving): hitte, water en fundering met de lagen van Kaart & 3D.
        path: "klimaat",
        lazy: async () => {
          const { Climate } = await import("@/pages/Climate");
          return { Component: Climate };
        },
      },
      {
        path: "beleid",
        element: (
          <Placeholder
            title="Beleid"
            description="Omgevingsplan, woonvisie en prestatieafspraken per gemeente."
            origin="Master-architectuur v4.0 §2 (Omgeving)."
            needs="een koppeling met het Omgevingsloket (DSO), en de prestatieafspraken als document van de corporatie."
          />
        ),
      },
      {
        path: "go",
        element: (
          <Placeholder
            title="PortefeuilleAtlas-GO"
            description="De mobiele veldinterface: werkvoorraad, zoeken via GPS, kaart of QR, inspecties, foto's, conditie en maatregelen, op dezelfde vastgoed-ID's."
            origin="Master-architectuur v4.0 §16."
            needs="een eigen fase met offline synchronisatie, een mobiele app of PWA en een afspraak over de omvang. Foto-opslag bestaat al (bijlagen bij feedback)."
          />
        ),
      },
      {
        path: "atlas-ai",
        element: (
          <Placeholder
            title="Atlas AI"
            description="De assistent die zoekt, uitlegt, signaleert en scenario's voorbereidt. De rekenengines rekenen, de AI legt uit."
            origin="Master-architectuur v4.0 §17 (blok F)."
            needs="een keuze voor het AI-platform (Azure-first), de tien vragen die de assistent moet kunnen beantwoorden en de rechten op de kennisbank."
          />
        ),
      },
      {
        path: "audittrail",
        element: (
          <Placeholder
            title="Audittrail"
            description="Wie wat heeft gewijzigd, berekend of vastgesteld, met reden en tijdstip."
            origin="Master-architectuur v4.0 §2 (Data & Beheer)."
            needs="alleen het scherm: het auditlog wordt al bijgehouden."
          />
        ),
      },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);
